import React, {
    ComponentPropsWithoutRef,
    ForwardedRef,
    Fragment,
    ReactNode,
    Suspense,
    forwardRef,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import * as Skeleton from '@givebutter/primitives/skeleton';
import { useMediaQuery } from '@givebutter/react-util';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from 'components/dialog';
import { AddGroupButton } from 'components/filter/filters-next/AddGroupButton';
import { DeleteFilter } from 'components/filter/filters-next/DeleteFilter';
import {
    FilterContext,
    FilterSearchKey,
    FilterSearchResource,
    IFilterContext,
} from 'components/filter/filters-next/FilterContext';
import { FilterListItem } from 'components/filter/filters-next/FilterListItem';
import { FilterListSearchSeparator } from 'components/filter/filters-next/FilterListSearchSeparator';
import { FilterListSectionTitle } from 'components/filter/filters-next/FilterListSectionTitle';
import { FilterSectionContext } from 'components/filter/filters-next/FilterSectionContext';
import { FilterStrategy } from 'components/filter/filters-next/FilterStrategy';
import { FilterTag } from 'components/filter/filters-next/FilterTag';
import { ReadableStrategy } from 'components/filter/filters-next/ReadableStrategy';
import { useFilterModulesStore } from 'components/filter/filters-next/filterModules';
import { withRelationalServerKey } from 'components/filter/filters-next/withRelationalServerKey';
import { RelationalServerKey, ServerKey } from 'components/filter/types';
import { SelectListItemSeparator } from 'components/select';
import { componentOrFragmentFactory } from 'components/util';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useForceRender } from 'customHooks/useForceRender';
import Fuse from 'fuse.js';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { omit } from 'lodash';
import { twJoin } from 'tailwind-merge';

import FuseResult = Fuse.FuseResult;

type FilterVariant = 'tag' | 'button';
type DynamicFilters = {
    [key in FilterSearchResource]?: ReactNode;
};

interface FilterProps
    extends Pick<
        IFilterContext,
        | 'resource'
        | 'cacheKey'
        | 'strategyType'
        | 'nodeId'
        | 'readOnly'
        | 'relationalServerKey'
        | 'readOnlyFilter'
        | 'isLegacy'
    > {
    children?: ReactNode;
    className?: string;
    noIcon?: boolean;
    popoverContentProps?: Popover.PopoverContentProps;
    variant?: FilterVariant;
    dynamicFilters?: DynamicFilters;
    addGroup?: boolean;
    forceRender?: () => void;
}

interface FilterListProps {
    relationalServerKey?: RelationalServerKey;
    children?: ReactNode;
}

const Filter = (props: FilterProps) => {
    const {
        resource,
        cacheKey,
        strategyType,
        relationalServerKey: relationalServerKeyConsumer,
        isLegacy: isLegacyConsumer = true,
        nodeId,
        children = 'More filters',
        readOnly,
        noIcon = false,
        popoverContentProps = {},
        className = '',
        variant = 'tag',
        readOnlyFilter,
        dynamicFilters,
        addGroup = true,
        forceRender,
    } = props;
    const filterModules = useFilterModulesStore(state => state.modules);
    const {
        sideOffset = 4,
        align = 'start',
        className: popoverContentClassName,
        ...restPopoverContentProps
    } = popoverContentProps;
    const isMobile = !!useMediaQuery(`(max-width: 640px)`);
    const [Root, Trigger, Portal, Content, Anchor] = isMobile
        ? [Dialog.Root, Dialog.Trigger, Dialog.Portal, Dialog.Content, EmptyDiv]
        : [Popover.Root, Popover.Trigger, Popover.Portal, Popover.Content, Popover.Anchor];
    const Overlay = componentOrFragmentFactory(Dialog.Overlay, isMobile);
    const PossibleBox = componentOrFragmentFactory(Box, !isMobile);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<ServerKey | undefined>(strategyType);
    const [isLegacy, setIsLegacy] = useState<boolean>(isLegacyConsumer);
    const [relationalServerKey, setRelationalServerKey] = useState<RelationalServerKey | undefined>(
        relationalServerKeyConsumer,
    );
    const [search, setSearch] = useState('');
    const [searchItems, setSearchItems] = useState<FuseResult<FilterSearchKey>[]>([]);

    const searchInstance = useMemo(
        () =>
            new Fuse<FilterSearchKey>([], {
                keys: ['serverKey', 'label', 'tags'],
                threshold: 0.3,
            }),
        [],
    );

    const handleSearch = useCallback(
        (value: string) => {
            setSearch(value);
            setSearchItems(searchInstance.search(value));
        },
        [searchInstance],
    );

    const clear = useCallback(() => {
        handleSearch('');
    }, [handleSearch]);

    const ctx = useMemo(
        () => ({
            resource,
            cacheKey,
            isOpen,
            isLegacy,
            strategyType: selectedFilter,
            relationalServerKey,
            nodeId,
            readOnly,
            search,
            searchInstance,
            searchItems,
            readOnlyFilter,
            setIsOpen,
            setSelectedFilter,
            setRelationalServerKey,
            setIsLegacy,
            clear,
            forceRender,
        }),
        [
            resource,
            cacheKey,
            isOpen,
            isLegacy,
            selectedFilter,
            relationalServerKey,
            nodeId,
            readOnly,
            search,
            searchInstance,
            searchItems,
            readOnlyFilter,
            clear,
            forceRender,
        ],
    );

    const Toggle = strategyType ? filterModules[strategyType].Toggle : false;
    const NextTrigger = Toggle || Trigger;
    const NextFilterTag = variant === 'button' ? EmptyDiv : FilterTag;
    const PossibleButton = componentOrFragmentFactory(Button, variant === 'button');

    if (!strategyType && !nodeId && readOnly) {
        return null;
    }

    return (
        <FilterContext.Provider value={ctx}>
            <Root
                open={readOnly || !!Toggle ? false : isOpen}
                onOpenChange={open => {
                    setIsOpen(open);
                    if (!open && !strategyType) {
                        clear();
                        setSelectedFilter(undefined);
                        setRelationalServerKey(undefined);
                    }
                }}
            >
                <Anchor asChild={true}>
                    <NextFilterTag
                        variant={nodeId ? 'selected' : 'unselected'}
                        data-intercom-target={
                            !nodeId && !strategyType
                                ? IntercomProductTourTargets.FiltersAddMoreButton
                                : undefined
                        }
                    >
                        {!readOnly && <DeleteFilter />}
                        <NextTrigger
                            className={twJoin(
                                readOnly && 'pointer-events-none',
                                className,
                                'flex min-h-[22px] items-center justify-center gap-1',
                            )}
                            asChild={variant === 'button'}
                        >
                            <PossibleButton color={'neutral'} variant={'outline'}>
                                {!noIcon && !nodeId && (
                                    <FontAwesomeIcon
                                        className={'text-sm font-medium text-gray-500'}
                                        icon={faCirclePlus}
                                    />
                                )}
                                <span className={'font-medium'}>
                                    {nodeId && strategyType ? (
                                        <Suspense fallback={<div className={'min-w-8'} />}>
                                            <ReadableStrategy strategy={strategyType} />
                                        </Suspense>
                                    ) : (
                                        <span className={'text-gray-500'}>{children}</span>
                                    )}
                                </span>
                            </PossibleButton>
                        </NextTrigger>
                    </NextFilterTag>
                </Anchor>
                <Portal>
                    <Fragment>
                        <Overlay />
                        <Content
                            className={twJoin(
                                'max-h-[--radix-popper-available-height] overflow-y-auto',
                                isMobile && 'w-auto min-w-80 p-0',
                                popoverContentClassName,
                                'z-pentOutermost',
                            )}
                            sideOffset={sideOffset}
                            align={align}
                            collisionPadding={{ top: 8 }}
                            {...restPopoverContentProps}
                        >
                            <PossibleBox className="overflow-hidden">
                                {selectedFilter ? (
                                    <Suspense
                                        fallback={
                                            <div className={'flex min-w-72 flex-col gap-4 p-4'}>
                                                <Skeleton.Bar orientation={'horizontal'} />
                                                <div className={'flex flex-col gap-2'}>
                                                    <Skeleton.Bar
                                                        fill={'full'}
                                                        orientation={'horizontal'}
                                                        className={'!h-6'}
                                                    />
                                                    <Skeleton.Bar
                                                        fill={'full'}
                                                        orientation={'horizontal'}
                                                        className={'!h-6'}
                                                    />
                                                    <Skeleton.Bar
                                                        fill={'full'}
                                                        orientation={'horizontal'}
                                                        className={'!h-6'}
                                                    />
                                                </div>
                                            </div>
                                        }
                                    >
                                        <FilterStrategy strategy={selectedFilter} />
                                    </Suspense>
                                ) : (
                                    <div
                                        role={'listbox'}
                                        className={
                                            'flex max-h-[calc(var(--radix-popover-content-available-height)-1rem)] flex-col'
                                        }
                                    >
                                        <input
                                            placeholder={'Search...'}
                                            value={search}
                                            onChange={e => handleSearch(e.target.value)}
                                            className={'border-b bg-white px-4 py-2 text-gray-500'}
                                        />
                                        <div
                                            className={
                                                'flex min-w-64 max-w-72 flex-col gap-0.5 overflow-y-auto overflow-x-hidden p-1.5'
                                            }
                                        >
                                            {addGroup && (
                                                <>
                                                    <AddGroupButton />
                                                    <SelectListItemSeparator className={'!my-1'} />
                                                </>
                                            )}
                                            {search.length > 0 && searchItems.length === 0 && (
                                                <div className={'px-4 py-2'}>
                                                    <p
                                                        className={
                                                            'text-sm font-medium text-gray-600'
                                                        }
                                                    >
                                                        No filters found
                                                    </p>
                                                </div>
                                            )}
                                            {resource === 'campaigns' && <CampaignsFilterList />}
                                            {resource === 'transactions' && (
                                                <TransactionsFilterList>
                                                    {dynamicFilters?.transaction}
                                                </TransactionsFilterList>
                                            )}
                                            {resource === 'tickets' && <TicketFilterList />}
                                            {resource === 'plans' && <TransactionPlanFilterList />}
                                            {resource === 'pledges' && <PledgePlanFilterList />}
                                            {resource === 'contacts' && (
                                                <ContactsFilterList
                                                    {...omit(dynamicFilters, 'contact')}
                                                >
                                                    {dynamicFilters?.contact}
                                                </ContactsFilterList>
                                            )}
                                            {resource === 'companies' && (
                                                <CompaniesFilterList
                                                    {...omit(dynamicFilters, 'company')}
                                                >
                                                    {dynamicFilters?.company}
                                                </CompaniesFilterList>
                                            )}
                                            {resource === 'tableFilters' &&
                                                dynamicFilters?.tableColumn}
                                        </div>
                                    </div>
                                )}
                            </PossibleBox>
                        </Content>
                    </Fragment>
                </Portal>
            </Root>
        </FilterContext.Provider>
    );
};

interface ContactsFilterListProps extends FilterListProps, Omit<DynamicFilters, 'contact'> {}

const ContactsFilterList = (props: ContactsFilterListProps) => {
    const { transactionSoftCredits, contactBulkActions, contactsWithoutName = false } = useFlags();
    const { children, transaction } = props;
    const { clear } = useContext(FilterContext);
    const ctx = useMemo(
        () => ({
            resource: 'contact' as FilterSearchResource,
        }),
        [],
    );
    const transactionsCtx = useMemo(
        () => ({
            resource: 'transaction' as FilterSearchResource,
        }),
        [],
    );
    const donorsearchCtx = useMemo(
        () => ({
            resource: 'donorsearch' as FilterSearchResource,
        }),
        [],
    );
    const memberCtx = useMemo(
        () => ({
            resource: 'member' as FilterSearchResource,
        }),
        [],
    );
    const pledgeCtx = useMemo(
        () => ({
            resource: 'pledge' as FilterSearchResource,
        }),
        [],
    );
    const planCtx = useMemo(
        () => ({
            resource: 'plan' as FilterSearchResource,
        }),
        [],
    );
    const ticketCtx = useMemo(
        () => ({
            resource: 'ticket' as FilterSearchResource,
        }),
        [],
    );

    return (
        <>
            <FilterSectionContext.Provider value={ctx}>
                <FilterListSectionTitle>Contact</FilterListSectionTitle>
                <FilterListItem serverKey={'total_contributions'} />
                {transactionSoftCredits && <FilterListItem serverKey={'total_soft_credits'} />}
                <FilterListItem serverKey={'recurring_contributions'} />
                <FilterListItem serverKey={'created_at'} />
                <FilterListItem serverKey={'company'} />
                <FilterListItem serverKey={'dob'} />
                <FilterListItem serverKey={'has_bidder_status'} />
                <FilterListItem serverKey={'widgetEmailSignUps'} />
                <FilterListItem serverKey={'trackable_message'} />
                <FilterListItem serverKey={'mailingsRecipients'} />
                <FilterListItem serverKey={'tags'} />
                <FilterListItem serverKey={'custom_fields'} />
                <FilterListItem serverKey={'contactAddress'} onClick={() => clear()} />
                <FilterListItem serverKey={'has_primary_email'} />
                <FilterListItem serverKey={'has_primary_phone'} />
                <FilterListItem serverKey={'activities'} />
                <FilterListItem serverKey={'lastCommunicatedAt'} />
                {contactsWithoutName && <FilterListItem serverKey={'has_name'} />}
                <FilterListItem serverKey={'lastDonatedAt'} />
                {contactBulkActions && <FilterListItem serverKey={'deletable'} />}
                {children}
            </FilterSectionContext.Provider>
            <FilterSectionContext.Provider value={donorsearchCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Donorsearch</FilterListSectionTitle>
            </FilterSectionContext.Provider>
            <DonorSearchFilterList />
            <FilterSectionContext.Provider value={transactionsCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Transactions</FilterListSectionTitle>
                <FilterListItem
                    serverKey={'table_count'}
                    relationalServerKey={'has_transactions'}
                />
            </FilterSectionContext.Provider>
            <TransactionsFilterList relationalServerKey={'has_transactions'}>
                {transaction}
            </TransactionsFilterList>
            <FilterSectionContext.Provider value={memberCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Team member</FilterListSectionTitle>
            </FilterSectionContext.Provider>
            <TeamMemberFilterList relationalServerKey={'has_campaign_members'} />
            <FilterSectionContext.Provider value={ticketCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Ticket</FilterListSectionTitle>
                <FilterListItem serverKey={'table_count'} relationalServerKey={'has_tickets'} />
            </FilterSectionContext.Provider>
            <TicketFilterList relationalServerKey={'has_tickets'} />
            <FilterSectionContext.Provider value={planCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Recurring plan</FilterListSectionTitle>
                <FilterListItem
                    serverKey={'table_count'}
                    relationalServerKey={'has_transaction_plans'}
                />
            </FilterSectionContext.Provider>
            <TransactionPlanFilterList relationalServerKey={'has_transaction_plans'} />
            <FilterSectionContext.Provider value={pledgeCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Pledge</FilterListSectionTitle>
                <FilterListItem serverKey={'table_count'} relationalServerKey={'has_pledges'} />
            </FilterSectionContext.Provider>
            <PledgePlanFilterList relationalServerKey={'has_pledges'} />
        </>
    );
};

interface CompaniesFilterListProps extends FilterListProps, Omit<DynamicFilters, 'contact'> {}

const CompaniesFilterList = (props: CompaniesFilterListProps) => {
    const { children } = props;
    const { clear } = useContext(FilterContext);
    const { transactionSoftCredits } = useFlags();
    const ctx = useMemo(
        () => ({
            resource: 'contact' as FilterSearchResource,
        }),
        [],
    );
    const transactionsCtx = useMemo(
        () => ({
            resource: 'transaction' as FilterSearchResource,
        }),
        [],
    );
    const donorsearchCtx = useMemo(
        () => ({
            resource: 'donorsearch' as FilterSearchResource,
        }),
        [],
    );
    const pledgeCtx = useMemo(
        () => ({
            resource: 'pledge' as FilterSearchResource,
        }),
        [],
    );
    const planCtx = useMemo(
        () => ({
            resource: 'plan' as FilterSearchResource,
        }),
        [],
    );

    return (
        <>
            <FilterSectionContext.Provider value={ctx}>
                <FilterListSectionTitle>Contact</FilterListSectionTitle>
                <FilterListItem serverKey={'total_contributions'} />
                {transactionSoftCredits && <FilterListItem serverKey={'total_soft_credits'} />}
                <FilterListItem serverKey={'recurring_contributions'} />
                <FilterListItem serverKey={'created_at'} />
                <FilterListItem serverKey={'widgetEmailSignUps'} />
                <FilterListItem serverKey={'tags'} />
                <FilterListItem serverKey={'custom_fields'} />
                <FilterListItem serverKey={'contactAddress'} onClick={() => clear()} />
                <FilterListItem serverKey={'activities'} />
                {children}
            </FilterSectionContext.Provider>
            <FilterSectionContext.Provider value={donorsearchCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Donorsearch</FilterListSectionTitle>
            </FilterSectionContext.Provider>
            <DonorSearchFilterList />

            <FilterSectionContext.Provider value={transactionsCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Transactions</FilterListSectionTitle>
                <FilterListItem
                    serverKey={'table_count'}
                    relationalServerKey={'has_transactions'}
                />
                <FilterListItem serverKey={'created_at'} relationalServerKey={'has_transactions'} />
                <FilterListItem serverKey={'amount'} relationalServerKey={'has_transactions'} />
                <FilterListItem
                    serverKey={'advancedContacts'}
                    relationalServerKey={'has_transactions'}
                />
                <FilterListItem serverKey={'campaigns'} relationalServerKey={'has_transactions'} />
                <FilterListItem serverKey={'funds'} relationalServerKey={'has_transactions'} />
                <FilterListItem serverKey={'status'} relationalServerKey={'has_transactions'} />
                <FilterListItem serverKey={'method'} relationalServerKey={'has_transactions'} />
                <FilterListItem
                    serverKey={'check_deposited'}
                    relationalServerKey={'has_transactions'}
                />
                <FilterListItem
                    serverKey={'creation_source'}
                    relationalServerKey={'has_transactions'}
                />
                <FilterListItem serverKey={'is'} relationalServerKey={'has_transactions'} />
                <FilterListItem
                    serverKey={'external_labels'}
                    relationalServerKey={'has_transactions'}
                />
                <FilterListItem
                    serverKey={'campaign_matches'}
                    relationalServerKey={'has_transactions'}
                />
            </FilterSectionContext.Provider>
            <FilterSectionContext.Provider value={planCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Recurring plan</FilterListSectionTitle>
                <FilterListItem
                    serverKey={'table_count'}
                    relationalServerKey={'has_transaction_plans'}
                />
            </FilterSectionContext.Provider>
            <TransactionPlanFilterList relationalServerKey={'has_transaction_plans'} />
            <FilterSectionContext.Provider value={pledgeCtx}>
                <FilterListSearchSeparator />
                <FilterListSectionTitle>Pledge</FilterListSectionTitle>
                <FilterListItem serverKey={'table_count'} relationalServerKey={'has_pledges'} />
            </FilterSectionContext.Provider>
            <PledgePlanFilterList relationalServerKey={'has_pledges'} />
        </>
    );
};

const CampaignsFilterList = () => {
    const ctx = useMemo(
        () => ({
            resource: 'campaign' as FilterSearchResource,
        }),
        [],
    );

    return (
        <FilterSectionContext.Provider value={ctx}>
            <FilterListItem serverKey={'donors'} />
            <FilterListItem serverKey={'raised'} />
            <FilterListItem serverKey={'goal'} />
            <FilterListItem serverKey={'type_friendly'} />
            <FilterListItem serverKey={'createdAt'} />
        </FilterSectionContext.Provider>
    );
};

const TransactionsFilterList = ({ children, relationalServerKey }: FilterListProps) => {
    const CurriedFilterListItem = withRelationalServerKey(relationalServerKey);
    const { transactionSoftCredits } = useFlags();

    const ctx = useMemo(
        () => ({
            resource: 'transaction' as FilterSearchResource,
        }),
        [],
    );

    return (
        <FilterSectionContext.Provider value={ctx}>
            <CurriedFilterListItem serverKey={'created_at'} />
            <CurriedFilterListItem serverKey={'amount'} />
            <CurriedFilterListItem serverKey={'advancedContacts'} />
            <CurriedFilterListItem serverKey={'campaigns'} />
            <CurriedFilterListItem serverKey={'type'} />
            <CurriedFilterListItem serverKey={'funds'} />
            <CurriedFilterListItem serverKey={'teams'} />
            <CurriedFilterListItem serverKey={'teamMembers'} />
            <CurriedFilterListItem serverKey={'status'} />
            <CurriedFilterListItem serverKey={'method'} />
            <CurriedFilterListItem serverKey={'check_deposited'} />
            <CurriedFilterListItem serverKey={'creation_source'} />
            <CurriedFilterListItem serverKey={'is'} />
            <CurriedFilterListItem serverKey={'external_labels'} />
            <CurriedFilterListItem serverKey={'has_auction_items'} />
            <CurriedFilterListItem serverKey={'contact_type'} />
            {transactionSoftCredits && !relationalServerKey && (
                <CurriedFilterListItem serverKey={'has_soft_credits'} />
            )}
            <CurriedFilterListItem serverKey={'campaign_matches'} />
            <CurriedFilterListItem serverKey={'has_internal_note'} />
            <CurriedFilterListItem serverKey={'has_public_message'} />
            <CurriedFilterListItem serverKey={'has_dedication'} />
            {children}
        </FilterSectionContext.Provider>
    );
};

const TeamMemberFilterList = ({ relationalServerKey }: FilterListProps) => {
    const CurriedFilterListItem = withRelationalServerKey(relationalServerKey);

    const ctx = useMemo(
        () => ({
            resource: 'member' as FilterSearchResource,
        }),
        [],
    );

    return (
        <FilterSectionContext.Provider value={ctx}>
            <CurriedFilterListItem serverKey={'table_count'} />
            <CurriedFilterListItem serverKey={'raised'} />
            <CurriedFilterListItem serverKey={'goal'} />
            <CurriedFilterListItem serverKey={'donors'} />
            <CurriedFilterListItem serverKey={'created_at'} />
        </FilterSectionContext.Provider>
    );
};

const TicketFilterList = ({ relationalServerKey }: FilterListProps) => {
    const CurriedFilterListItem = withRelationalServerKey(relationalServerKey);

    const ctx = useMemo(
        () => ({
            resource: 'ticket' as FilterSearchResource,
        }),
        [],
    );

    return (
        <FilterSectionContext.Provider value={ctx}>
            <CurriedFilterListItem serverKey={'created_at'} />
            <CurriedFilterListItem serverKey={'status'} />
            <CurriedFilterListItem serverKey={'ticket_types'} />
            <CurriedFilterListItem serverKey={'campaigns'} />
            <CurriedFilterListItem serverKey={'custom_fields'} />
            <CurriedFilterListItem serverKey={'ticketBundles'} />
            <CurriedFilterListItem serverKey={'advancedEvents'} />
            <CurriedFilterListItem serverKey={'discountCodes'} />
        </FilterSectionContext.Provider>
    );
};

const TransactionPlanFilterList = ({ relationalServerKey }: FilterListProps) => {
    const CurriedFilterListItem = withRelationalServerKey(relationalServerKey);

    const ctx = useMemo(
        () => ({
            resource: 'plan' as FilterSearchResource,
        }),
        [],
    );

    return (
        <FilterSectionContext.Provider value={ctx}>
            <CurriedFilterListItem serverKey={'amount'} />
            <CurriedFilterListItem serverKey={'startAt'} />
            <CurriedFilterListItem serverKey={'nextBillDate'} />
            <CurriedFilterListItem serverKey={'status'} />
            <CurriedFilterListItem serverKey={'method'} />
            <CurriedFilterListItem serverKey={'funds'} />
            <CurriedFilterListItem serverKey={'frequency'} />
        </FilterSectionContext.Provider>
    );
};

const PledgePlanFilterList = ({ relationalServerKey }: FilterListProps) => {
    const CurriedFilterListItem = withRelationalServerKey(relationalServerKey);

    const ctx = useMemo(
        () => ({
            resource: 'pledge' as FilterSearchResource,
        }),
        [],
    );

    return (
        <FilterSectionContext.Provider value={ctx}>
            <CurriedFilterListItem serverKey={'amount'} />
            <CurriedFilterListItem serverKey={'amountFulfilled'} />
            <CurriedFilterListItem serverKey={'remainingAmount'} />
            <CurriedFilterListItem serverKey={'createdAt'} />
            <CurriedFilterListItem serverKey={'firstInstallmentDate'} />
            <CurriedFilterListItem serverKey={'nextInstallmentDate'} />
            <CurriedFilterListItem serverKey={'fullfillmentDate'} />
            <CurriedFilterListItem serverKey={'statusFriendly'} />
            <CurriedFilterListItem serverKey={'type'} />
            <CurriedFilterListItem serverKey={'funds'} />
            <CurriedFilterListItem serverKey={'frequency'} />
        </FilterSectionContext.Provider>
    );
};

const DonorSearchFilterList = () => {
    const ctx = useMemo(
        () => ({
            resource: 'donorsearch' as FilterSearchResource,
        }),
        [],
    );

    return (
        <FilterSectionContext.Provider value={ctx}>
            <FilterListItem serverKey={'DS_Rating'} />
            <FilterListItem serverKey={'MajorGiftLikelihood'} />
            <FilterListItem serverKey={'LargestGiftLow'} />
            <FilterListItem serverKey={'LikelyMatchesTotal'} />
            <FilterListItem serverKey={'AnnualFundLikelihood'} />
        </FilterSectionContext.Provider>
    );
};

const EmptyDiv = forwardRef(
    (props: ComponentPropsWithoutRef<'div'>, ref: ForwardedRef<HTMLDivElement>) => (
        <div ref={ref} {...props} />
    ),
);

const withForceRenderer = <T,>(WrappedComponent: React.ComponentType<T>) => {
    const Wrapped = (props: Omit<T, keyof FilterProps>) => {
        const { key, forceRender } = useForceRender();

        return <WrappedComponent key={key} forceRender={forceRender} {...(props as T)} />;
    };

    Wrapped.displayName = `WithForceRenderer(${
        WrappedComponent.displayName || WrappedComponent.name
    }`;

    return Wrapped;
};

const EnhancedFilter = withForceRenderer(Filter) as typeof Filter;

export { EnhancedFilter as Filter };
export type { FilterProps };
